import { DefaultButton } from 'components/button/DefaultButton';
import { TextInput } from 'components/forms/TextInput';
import { ValidatableLabel } from 'components/forms/ValidatableLabel';
import { Spinner } from 'components/spinner/Spinner';
import { sdClient } from 'contexts/SDKClientContext';
import { Heading, LoginForm, StyledTextInput } from 'pages/Login/Login';
import { sdUserEmailRules } from 'pages/Login/shared';
import { useState } from 'react';
import { useForm, UseFormRegister } from 'react-hook-form';
import { SecondaryText } from 'styles/theme_styles/text';
import { useDebounceOnChangeValidation } from 'utils/form';

export type ResetPasswordScreenProps = {
    register: UseFormRegister<LoginForm>;
    email: string;
};

export type ResetPasswordState =
    | {
          name: 'resetPassword';
      }
    | {
          name: 'emailSent';
          sentToEmail: string;
      }
    | {
          name: 'error';
      };

export function ResetPasswordScreen({
    email,
    register: registerLoginFormInput,
}: ResetPasswordScreenProps) {
    type ResetPasswordForm = {
        resetEmail: string;
    };
    const [state, setState] = useState<ResetPasswordState>({ name: 'resetPassword' });
    const {
        register,
        handleSubmit,
        formState: { errors, isSubmitted, isValid, isValidating, isSubmitting },
    } = useForm<ResetPasswordForm>({
        mode: 'onSubmit',
        reValidateMode: 'onChange',
        defaultValues: {
            resetEmail: email,
        },
    });
    const emailValidationProps = useDebounceOnChangeValidation(
        registerLoginFormInput('email')
    );

    const onResetPassword = async (data: ResetPasswordForm) => {
        try {
            const authProvider = await sdClient.getAuthProvider({
                emailAddress: data.resetEmail,
            });
            if (!authProvider.AuthenticationProvider) {
                // Future improvement: show an error here?
                return;
            }

            await sdClient.resetPasswordSD4({ body: data.resetEmail });
            setState({ name: 'emailSent', sentToEmail: data.resetEmail });
        } catch {
            setState({ name: 'error' });
        }
    };

    if (state.name === 'error') {
        return (
            <>
                <Heading>Something went wrong</Heading>
                <SecondaryText>
                    The request was not handled successfully. Please contact KCF Support
                    if the problem persists.
                </SecondaryText>
            </>
        );
    }

    if (state.name === 'emailSent') {
        return (
            <>
                <Heading>Email sent!</Heading>
                <SecondaryText>
                    If an account exists, a temporary link to reset your password will be
                    sent to <strong>{state.sentToEmail}</strong>. For security purposes,
                    the link will expire in 24 hours.
                </SecondaryText>
                <strong>Don&apos;t see the email?</strong>
                <SecondaryText>
                    Be sure to check your spam folder if it doesn&apos;t appear within a
                    few minutes.
                </SecondaryText>
            </>
        );
    }

    const isActionable = (!isSubmitted || isValid) && !(isValidating || isSubmitting);

    return (
        <>
            <Heading className="mb-8">Need to reset your password?</Heading>
            <SecondaryText>
                Please enter the email address associated with your SMARTdiagnostics
                account and we&apos;ll send you a link to reset your password.
            </SecondaryText>
            <form className="mt-40" onSubmit={handleSubmit(onResetPassword)}>
                {/* The hidden input allows the email/form state to get carried across renders */}
                <TextInput type="hidden" {...emailValidationProps} />
                <ValidatableLabel
                    label="Email Address"
                    className="mb-16"
                    error={errors.resetEmail}
                >
                    <StyledTextInput
                        type="email"
                        placeholder="name@company.com"
                        autofocus
                        invalid={errors.resetEmail !== undefined}
                        {...register('resetEmail', sdUserEmailRules)}
                    />
                </ValidatableLabel>
                <footer>
                    {isSubmitting ? <Spinner /> : null}
                    <DefaultButton disabled={!isActionable}>Send Email</DefaultButton>
                </footer>
            </form>
        </>
    );
}
